// App.js
import React, { useContext, useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import PrivateRoute from './PrivateRoute';
import { UserProvider } from './contexts/UserContext';
import { MembershipProvider, MembershipContext } from './contexts/MembershipContext';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import DelayedFallback from './components/DelayedFallback';
import membresias from './data/membresias.json';

import AnalyticsTracker from './components/AnalyticsTracker'; 
import PwaInstallBanner from './components/PwaInstallBanner';

// Lazy imports (los tuyos)
const CreatePasswordPage = React.lazy(() => import('./pages/CreatePasswordPage'));
const NoDisponibleDemoPage = React.lazy(() => import('./pages/NoDisponibleDemoPage'));
const DemoPage = React.lazy(() => import('./pages/DemoPage'));
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const MigrationPage = React.lazy(() => import('./pages/MigrationPage'));
const ForgotPasswordPage = React.lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/ResetPasswordPage'));
const LogoutPage = React.lazy(() => import('./pages/LogoutPage'));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const BasePage = React.lazy(() => import('./pages/BasePage'));
const NoticiasPage = React.lazy(() => import('./pages/NoticiasPage'));
const KioskoPage = React.lazy(() => import('./pages/KioskoPage'));
const KioskoReadPage = React.lazy(() => import('./pages/KioskoReadPage'));
const KioskoReadUPCPage = React.lazy(() => import('./pages/KioskoReadUPCPage'));
const KioskoSeccionesPage = React.lazy(() => import('./pages/KioskoSeccionesPage'));
const EbookPage = React.lazy(() => import('./pages/EbookPage'));
const EbookCategoriesPage = React.lazy(() => import('./pages/EbookCategoriesPage'));
const DescargasPage = React.lazy(() => import('./pages/DescargasPage'));
const SoportePage = React.lazy(() => import('./pages/SoportePage'));
const UserProfilePage = React.lazy(() => import('./pages/UserProfilePage'));
const EstadosfinancierospdfPage = React.lazy(() => import('./pages/EstadosfinancierospdfPage'));
const ConsultoresAiPage = React.lazy(() => import('./pages/ConsultoresAiPage'));
const ConsultorAiPage = React.lazy(() => import('./pages/ConsultorAiPage'));
// --- V3 LABS IMPORTS ---
const ConsultoresAiV3Page = React.lazy(() => import('./pages/ConsultoresAiV3Page'));
const ConsultorAiV3Page = React.lazy(() => import('./pages/ConsultorAiV3Page'));
const BaseAiPage = React.lazy(() => import('./pages/BaseAiPage'));

const PDFViewerPage = React.lazy(() => import('./pages/PDFViewerPage'));

// Analytics
const HomeAnalyticsPage = React.lazy(() => import('./analytics/HomeAnalyticsPage'));
const DashboardAnalyticsPage = React.lazy(() => import('./analytics/DashboardAnalyticsPage'));
const DashboardToponlinePage = React.lazy(() => import('./pages/DashboardToponlinePage'));
const MisDashboardsAnalyticsPage = React.lazy(() => import('./analytics/MisDashboardsAnalyticsPage'));
const ComunidadAnalyticsPage = React.lazy(() => import('./analytics/ComunidadAnalyticsPage'));
const DashboardsExclusivosAnalyticsPage = React.lazy(() => import('./analytics/DashboardsExclusivosAnalyticsPage'));
const MiOrganizacionAnalyticsPage = React.lazy(() => import('./analytics/MiOrganizacionAnalyticsPage'));
const AnalyticsUserProfilePage = React.lazy(() => import('./analytics/UserProfilePage'));
const AyudaAnalyticsPage = React.lazy(() => import('./analytics/AyudaAnalyticsPage'));

// Proyectos
const MapasPDFPage = React.lazy(() => import('./pages/proyectos/MapasPDFPage'));
const MinasPage = React.lazy(() => import('./pages/proyectos/MinasPage'));
const MinaPage = React.lazy(() => import('./pages/proyectos/MinaPage'));
const PerfilesProyectosPage = React.lazy(() => import('./pages/proyectos/PerfilesProyectosPage'));
const PerfilProyectoPage  = React.lazy(() => import('./pages/proyectos/PerfilProyectoPage'));
const PerfilesProyectosInfraestructuraPage = React.lazy(() => import('./pages/proyectos/PerfilesProyectosInfraestructuraPage'));
const PerfilProyectoInfraestructuraPage  = React.lazy(() => import('./pages/proyectos/PerfilProyectoInfraestructuraPage'));

// --- NUEVO: componente que gestiona clases del <body> ---
function BodyClassManager() {
  const { selectedMembership } = useContext(MembershipContext);
  const location = useLocation();

  useEffect(() => {
    const isAuthRoute = location.pathname === '/login' || location.pathname === '/logout';

    // Limpia todas las clases actuales
    document.body.className = '';

    if (isAuthRoute) {
      // Para login/logout, solo esta clase
      document.body.classList.add('top_online_estandar');
      return () => { document.body.className = ''; };
    }

    if (selectedMembership) {
      const selectedAlias = membresias?.membresias?.find(
        (m) => m.nombre === selectedMembership
      )?.alias;

      if (selectedAlias) {
        document.body.classList.add(selectedAlias);
      }
    }

    // Cleanup al desmontar/cambiar ruta
    return () => { document.body.className = ''; };
  }, [location.pathname, selectedMembership]);

  return null; // No renderiza UI
}

// --- Componente de rutas (sin hooks de router) ---
function MainRoutes() {
  return (
    <Suspense fallback={<DelayedFallback delay={1000} fallback={<div>Loading...</div>} />}>
      <Routes>
        <Route path="/demo" element={<DemoPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/migration" element={<MigrationPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/pdf-viewer" element={<PDFViewerPage />} />

        <Route path="/create-password" element={<PrivateRoute />}>
          <Route index element={<CreatePasswordPage />} />
        </Route>

        <Route path="/no-disponible-demo" element={<PrivateRoute />}>
          <Route index element={<NoDisponibleDemoPage />} />
        </Route>

        <Route path="/dashboard" element={<PrivateRoute />}>
          <Route index element={<DashboardPage />} />
        </Route>

        <Route path="/noticias" element={<PrivateRoute />}>
          <Route index element={<NoticiasPage />} />
        </Route>

        <Route path="/descargas" element={<PrivateRoute />}>
          <Route index element={<DescargasPage />} />
        </Route>

        <Route path="/soporte" element={<PrivateRoute />}>
          <Route index element={<SoportePage />} />
        </Route>

        <Route path="/user-profile-page" element={<PrivateRoute />}>
          <Route index element={<UserProfilePage />} />
        </Route>

        <Route path="/estadosfinancierospdf" element={<PrivateRoute />}>
          <Route index element={<EstadosfinancierospdfPage />} />
        </Route>

        <Route path="/kiosko" element={<PrivateRoute />}>
          <Route index element={<KioskoPage />} />
        </Route>

        <Route path="/kiosko-secciones/:id" element={<PrivateRoute />}>
          <Route index element={<KioskoSeccionesPage />} />
        </Route>

        <Route path="/kiosko-read/:id" element={<PrivateRoute />}>
          <Route index element={<KioskoReadPage />} />
        </Route>

        <Route path="/kiosko-read-upc/:id" element={<PrivateRoute />}>
          <Route index element={<KioskoReadUPCPage />} />
        </Route>

        <Route path="/ebook/:id" element={<PrivateRoute />}>
          <Route index element={<EbookPage />} />
        </Route>

        <Route path="/ebook-categorias/:categoria" element={<PrivateRoute />}>
          <Route index element={<EbookCategoriesPage />} />
        </Route>

        {/* Analytics */}
        <Route path="/home-analytics" element={<PrivateRoute />}>
          <Route index element={<HomeAnalyticsPage />} />
        </Route>
        <Route path="/dashboard-analytics/:uuid" element={<PrivateRoute />}>
          <Route index element={<DashboardAnalyticsPage />} />
        </Route>
        <Route path="/dashboard-embed/:uuid" element={<PrivateRoute />}>
          <Route index element={<DashboardAnalyticsPage layoutMode="embed" />} />
        </Route>
        <Route path="/dashboard-toponline/:uuid" element={<PrivateRoute />}>
          <Route index element={<DashboardToponlinePage />} />
        </Route>
        <Route path="/mis-dashboards-analytics" element={<PrivateRoute />}>
          <Route index element={<MisDashboardsAnalyticsPage />} />
        </Route>
        <Route path="/comunidad-analytics" element={<PrivateRoute />}>
          <Route index element={<ComunidadAnalyticsPage />} />
        </Route>
        <Route path="/mi-organizacion-analytics" element={<PrivateRoute />}>
          <Route index element={<MiOrganizacionAnalyticsPage />} />
        </Route>
        <Route path="/dashboards-exclusivos-analytics" element={<PrivateRoute />}>
          <Route index element={<DashboardsExclusivosAnalyticsPage />} />
        </Route>
        <Route path="/analytics-user-profile" element={<PrivateRoute />}>
          <Route index element={<AnalyticsUserProfilePage />} />
        </Route>
        <Route path="/ayuda-analytics" element={<PrivateRoute />}>
          <Route index element={<AyudaAnalyticsPage />} />
        </Route>

        <Route path="/base/:table" element={<PrivateRoute />}>
          <Route index element={<BasePage />} />
        </Route>

        <Route path="/consultores-ai" element={<PrivateRoute />}>
          <Route index element={<ConsultoresAiPage />} />
        </Route>
        <Route path="/consultor-ai/:uuid" element={<PrivateRoute />}>
          <Route index element={<ConsultorAiPage />} />
        </Route>

        {/* --- RUTAS V3 (LABS) --- */}
        <Route path="/consultores-ai-v3" element={<PrivateRoute />}>
          <Route index element={<ConsultoresAiV3Page />} />
        </Route>
        <Route path="/consultor-ai-v3/:uuid" element={<PrivateRoute />}>
          <Route index element={<ConsultorAiV3Page />} />
        </Route>
        <Route path="/base-ai/:table" element={<PrivateRoute />}>
            <Route index element={<BaseAiPage />} />
        </Route>

        {/* Proyectos */}
        <Route path="/mapaspdf" element={<PrivateRoute />}>
            <Route index element={<MapasPDFPage />} />
        </Route>
        
        <Route path="/minas" element={<PrivateRoute />}>
            <Route index element={<MinasPage />} />
        </Route>
        <Route path="/mina/:id" element={<PrivateRoute />}>
            <Route index element={<MinaPage />} />
        </Route>

        <Route path="/perfiles-proyectos" element={<PrivateRoute />}>
            <Route index element={<PerfilesProyectosPage />} />
        </Route>
        <Route path="/perfil/:id" element={<PrivateRoute />}>
            <Route index element={<PerfilProyectoPage />} />
        </Route>

        <Route path="/perfiles-infraestructura-proyectos" element={<PrivateRoute />}>
            <Route index element={<PerfilesProyectosInfraestructuraPage />} />
        </Route>
        <Route path="/perfil-infraestructura/:id" element={<PrivateRoute />}>
            <Route index element={<PerfilProyectoInfraestructuraPage />} />
        </Route>

        <Route path="/logout" element={<PrivateRoute />}>
          <Route index element={<LogoutPage />} />
        </Route>

        {/* Default */}
        <Route path="/" element={<LoginPage />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <UserProvider>
      <MembershipProvider>
        <Router>
          <AnalyticsTracker /> 
          <BodyClassManager />
          <MainRoutes />
          <PwaInstallBanner />
          <ToastContainer />
        </Router>
      </MembershipProvider>
    </UserProvider>
  );
}

export default App;
